import _extends from '@babel/runtime/helpers/extends';
import React__default, { useMemo, useRef, useEffect, useCallback } from 'react';
import { Transition } from 'react-transition-group';
import { useTheme } from 'styled-components';
import { O as Overlay } from './Overlay-CLnEnNIS.js';
import { O as OnCloseContext } from './Context-dY1G4PQg.js';
import { u as useOpenSheets } from './SheetContext-DthRk6uR.js';
import { g as getExtraSpace } from './getExtraSpace-DIh3XujE.js';
import { v as v4 } from '../3rdPartyModules/uuid/dist/esm-browser/v4-CYEyAeRz.js';
import { P as Portal } from '../Portal/Portal-CfuGnhf3.js';
import { P as PreventScroll } from '../LocalModules/storm-ui-utils/dist/esm/PreventScroll-C7cxX_Px-zZoGpe3b.js';
import { T as TaktIdProvider } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';
import { g as getElementFromRef } from '../LocalModules/storm-ui-utils/dist/esm/getElementFromRef-DUWqD2Fg-DUWqD2Fg.js';

const defaultTransitionDuration = 200; // milliseconds

const teardown = toggleEl => {
  // Bring focus back to the toggle element
  if (toggleEl !== undefined) {
    const element = getElementFromRef(toggleEl);
    if (element !== null && element !== void 0 && element.focus) {
      element.focus();
    }
  }
};
const Sheet = ({
  sheetElementId,
  children,
  className = '',
  closeButton,
  closeButtonProps,
  isOpen,
  onClose,
  padding = 'base',
  side = 'bottom',
  withCloseButton = true,
  maxSize = 'default',
  taktId,
  taktValue,
  preventUnmountOnClose = false,
  transitionDuration = defaultTransitionDuration,
  toggleEl,
  ...rest
}) => {
  const theme = /*#__PURE__*/useTheme();
  const zIndex = theme.elevation.zIndex.layer4;
  const uuid = useMemo(() => {
    // Will get "crypto.getRandomValues() not supported." error in some test environments.
    // https://github.com/uuidjs/uuid/blob/a505f01e59d8a5e432ab3355c5522bea055a8c27/src/rng-browser.ts#L13
    if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
      const rng = () => Uint8Array.of(...[...Array(16)].map(() => Math.floor(Math.random() * 256)));
      return v4({
        rng
      });
    }
    return v4();
  }, []);
  const {
    openedSheets,
    addOpenedSheet,
    removeOpenedSheet
  } = useOpenSheets();
  const extraSpace = getExtraSpace({
    openedSheets,
    uuid,
    side,
    zIndex
  });
  const modalContainerRef = useRef(null);
  useEffect(() => () => {
    // focus on toggle element on unmount
    teardown(toggleEl);
  }, [toggleEl]);
  useEffect(() => () => {
    removeOpenedSheet(uuid);
  }, [uuid, removeOpenedSheet]);
  const handleGetSheetSize = useCallback((size, sheetSide) => {
    addOpenedSheet({
      side: sheetSide,
      id: uuid,
      size,
      zIndex
    });
  }, [addOpenedSheet, uuid, zIndex]);
  return /*#__PURE__*/React__default.createElement(Transition, {
    timeout: {
      enter: 0,
      exit: transitionDuration
    },
    appear: true,
    in: isOpen,
    onEntered: () => {
      addOpenedSheet({
        side,
        id: uuid,
        zIndex
      });
    },
    onExit: () => {
      removeOpenedSheet(uuid);
      teardown(toggleEl);
    },
    nodeRef: modalContainerRef,
    unmountOnExit: !preventUnmountOnClose
  }, state => {
    if (state === 'exited' && !preventUnmountOnClose) return null;
    return /*#__PURE__*/React__default.createElement(Portal, {
      containerId: sheetElementId
    }, /*#__PURE__*/React__default.createElement(PreventScroll, {
      isScrollEnabled: state === 'exited' && preventUnmountOnClose
    }, /*#__PURE__*/React__default.createElement(TaktIdProvider, {
      taktId: taktId,
      taktValue: taktValue,
      fallbackId: createStormTaktId('sheet')
    }, /*#__PURE__*/React__default.createElement(OnCloseContext.Provider, {
      value: onClose
    }, /*#__PURE__*/React__default.createElement(Overlay, _extends({}, rest, {
      zIndex: zIndex,
      className: className,
      closeButton: closeButton,
      closeButtonProps: closeButtonProps,
      modalContainerRef: modalContainerRef,
      onClose: onClose,
      padding: padding,
      state: state,
      side: side,
      withCloseButton: withCloseButton,
      maxSize: maxSize,
      extraSpace: extraSpace,
      transitionDuration: transitionDuration,
      onGetSheetSize: handleGetSheetSize
    }), children)))));
  });
};

export { Sheet as S };
//# sourceMappingURL=Sheet-CwAzJORl.js.map
